// src/components/batches/Batches.js
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { batchAPI, productAPI } from "../../services";
import BatchView from "./BatchView";
import BatchDetailView from "./BatchDetailView";

export default function Batches({
  products: propProducts = [],
}) {
  const [batches, setBatches] = useState([]);
  const [suppliers, ] = useState([]);
  const [products, setProducts] = useState(propProducts);
  const [currentView, setCurrentView] = useState("list"); // "list" or "detail"
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const hasFetchedInitialData = useRef(false);


  // Load products from API if not provided as props
  const fetchProducts = async () => {
    if (propProducts.length > 0) {
      setProducts(propProducts);
      return;
    }

    try {
      const response = await productAPI.getProductsList({
        page_size: 100 // Get more products for dropdown
      });
      
      if (response && Array.isArray(response.results)) {
        setProducts(response.results);
      } else if (response && Array.isArray(response)) {
        setProducts(response);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      // Don't show error toast as products are optional for batches
      setProducts([]);
    }
  };

  // Load batches from API
  const fetchBatches = async (search = "", page = 1) => {
    setCurrentPage(page);
    setLoading(true);
    try {
      const response = await batchAPI.getBatchesList({
        search,
        page,
        page_size: 25,
      });

      if (response && Array.isArray(response.results)) {
        setBatches(response.results);
        setTotalCount(response.count || 0);
      } else if (response && Array.isArray(response)) {
        setBatches(response);
        setTotalCount(response.length || 0);
      } else {
        setBatches([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch batches. Please try again.";
      toast.error(errorMessage);
      
      // Fallback to localStorage
      const stored = localStorage.getItem("batches");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setBatches(parsed);
          setTotalCount(parsed.length);
        } catch {
          setBatches([]);
          setTotalCount(0);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent duplicate API calls in React StrictMode
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    
    // Defer loading Supplier until Add Batch is clicked (BatchForm mounts)
    fetchProducts();
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save to localStorage as backup
  useEffect(() => {
    if (batches.length > 0) {
      localStorage.setItem("batches", JSON.stringify(batches));
    }
  }, [batches]);

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setCurrentView("detail");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setSelectedBatch(null);
  };

  const handleEditBatch = (updatedBatch) => {
    setBatches((prev) =>
      prev.map((b) => (b.id === updatedBatch.id ? updatedBatch : b))
    );
    setSelectedBatch(updatedBatch);
  };

  const handleAddStock = (batchId, stock) => {
    setBatches((prev) =>
      prev.map((b) =>
        b.id === batchId ? { ...b, stocks: [...(b.stocks || []), stock] } : b
      )
    );
    // Update selected batch if it's the current one
    if (selectedBatch && selectedBatch.id === batchId) {
      setSelectedBatch((prev) => ({
        ...prev,
        stocks: [...(prev.stocks || []), stock],
      }));
    }
  };

  const handleEditStock = (batchId, updatedStock) => {
    setBatches((prev) =>
      prev.map((b) =>
        b.id === batchId
          ? {
              ...b,
              stocks: b.stocks.map((s) =>
                s.id === updatedStock.id ? updatedStock : s
              ),
            }
          : b
      )
    );
    // Update selected batch if it's the current one
    if (selectedBatch && selectedBatch.id === batchId) {
      setSelectedBatch((prev) => ({
        ...prev,
        stocks: prev.stocks.map((s) =>
          s.id === updatedStock.id ? updatedStock : s
        ),
      }));
    }
  };

  const handleDeleteStock = (batchId, stockId) => {
    setBatches((prev) =>
      prev.map((b) =>
        b.id === batchId
          ? { ...b, stocks: b.stocks.filter((s) => s.id !== stockId) }
          : b
      )
    );
    // Update selected batch if it's the current one
    if (selectedBatch && selectedBatch.id === batchId) {
      setSelectedBatch((prev) => ({
        ...prev,
        stocks: prev.stocks.filter((s) => s.id !== stockId),
      }));
    }
  };

  return (
    <div className="batches-container">
      {currentView === "list" && (
        <BatchView
          onViewBatch={handleViewBatch}
          suppliers={suppliers}
          products={products}
          loading={loading}
          onRefresh={fetchBatches}
          batches={batches}
          totalCount={totalCount}
          currentPage={currentPage}
        />
      )}

      {currentView === "detail" && selectedBatch && (
        <BatchDetailView
          batch={selectedBatch}
          onBack={handleBackToList}
          onEditBatch={handleEditBatch}
          onAddStock={handleAddStock}
          onEditStock={handleEditStock}
          onDeleteStock={handleDeleteStock}
          products={products}
          suppliers={suppliers}
          allBatches={batches}
        />
      )}
    </div>
  );
}
